import GradeIcon from '@mui/icons-material/Grade';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { useEffect, useRef, useState } from 'react';
import popularProductsApi from '../../../api/popularProduct.tsx';
import { allOption } from '../../../common/constants.tsx';
import { hasAdminRole } from '../../../common/roleFunctions.tsx';
import { isStaff } from '../../../common/typeAssertionFunctions.tsx';
import { PopularProduct, Product, Staff } from '../../../common/types';
import useAuthStore from '../../../datastore/useAuth.tsx';
import usePopularProducts from '../../../datastore/usePopularProducts.tsx';
import useProducts from '../../../datastore/useProducts.tsx';
import useAlert from '../../../hooks/useAlert.tsx';
import { AdminHeader } from '../components/AdminHeader.tsx';
import { PopularProductFilter } from './components/PopularProductFilter.tsx';
import { PopularProductTable } from './components/PopularProductTable.tsx';

const productsPerPage = 200;

const addPopularProduct = (
  product: Product,
  popularProducts: PopularProduct[],
  newDisplayOrder: number,
  currentUser: Staff | null,
): PopularProduct[] => {
  const updatedData = [...popularProducts];
  const foundProductIndex = updatedData.findIndex(
    (p) => p.productId === product.id,
  );

  if (foundProductIndex !== -1) {
    updatedData[foundProductIndex] = {
      ...updatedData[foundProductIndex],
      isActive: true,
      displayOrder: newDisplayOrder,
    };
  } else {
    const newPopularProduct: PopularProduct = {
      id: 0,
      displayOrder: newDisplayOrder,
      productId: product.id,
      siteId: isStaff(currentUser) ? currentUser.siteId : 0,
      product: product,
      isActive: true,
    };
    updatedData.push(newPopularProduct);
  }
  return updatedData;
};

const changeOrder = (
  popularProducts: PopularProduct[],
  productId: number,
  newOrder: number,
): PopularProduct[] => {
  const activeProducts = popularProducts.filter((p) => p.isActive);

  const finalNewOrder = Math.max(1, Math.min(newOrder, activeProducts.length));

  const productToMove = activeProducts.find((p) => p.productId === productId);
  if (!productToMove) {
    return [];
  }

  const oldOrder = productToMove.displayOrder;

  const updatedProducts = activeProducts.map((p) => {
    if (p.productId === productId) {
      return { ...p, displayOrder: finalNewOrder };
    } else if (
      oldOrder < finalNewOrder &&
      p.displayOrder > oldOrder &&
      p.displayOrder <= finalNewOrder
    ) {
      return { ...p, displayOrder: p.displayOrder - 1 };
    } else if (
      oldOrder > finalNewOrder &&
      p.displayOrder >= finalNewOrder &&
      p.displayOrder < oldOrder
    ) {
      return { ...p, displayOrder: p.displayOrder + 1 };
    }
    return p;
  });

  const sortedProducts = updatedProducts
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((p, index) => ({ ...p, displayOrder: index + 1 }));

  const updated = [
    ...sortedProducts,
    ...popularProducts.filter((p) => !p.isActive),
  ];
  return updated;
};

const removePopularProduct = (
  product: Product,
  popularProducts: PopularProduct[],
): PopularProduct[] => {
  const updatedData = popularProducts.map((p) =>
    p.productId === product.id ? { ...p, isActive: false, displayOrder: 0 } : p,
  );
  const inactiveData = updatedData.filter((p) => !p.isActive);

  const activeProducts = updatedData
    .filter((p) => p.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((p, index) => ({ ...p, displayOrder: index + 1 }));
  const updated = [...activeProducts, ...inactiveData];
  return updated;
};

export default function PopularProductsPage() {
  const { currentUser } = useAuthStore();
  const { products = [], loading: productsLoading, fetchProducts } = useProducts();
  const {
    popularProducts = [],
    loading: popularProductsLoading,
    fetchPopularProducts,
  } = usePopularProducts();
  const { showAlert } = useAlert();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [filterText, setFilterText] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    // reload all the data. the hooks dont automatically load fresh data and so miss when a product is enabled/disabled
    fetchProducts()
    fetchPopularProducts()
  }, [])

  const isLoading = popularProductsLoading || productsLoading;

  if (!hasAdminRole(currentUser)) return null;

  function handleAddProduct(product: Product) {
    const activeProducts = popularProducts.filter((f) => f.isActive);
    const newDisplayOrder = activeProducts.length + 1;
    if (newDisplayOrder > 5) {
      showAlert(
        'You can\'t have more than 5 active popular products.',
        'warning',
      );
      return;
    }

    const updatedData = addPopularProduct(
      product,
      popularProducts,
      newDisplayOrder,
      currentUser as Staff | null,
    );
    handleSaveChanges(updatedData);
  }

  function handleOrderChange(productId: number, newOrder: number) {
    if (!popularProducts.length) {
      return;
    }
    const updated = changeOrder(popularProducts, productId, newOrder);
    if (updated.length) {
      handleSaveChanges(updated);
    }
  }

  function getUniqueProductCategories() {
    const categories =
      products.map((product) => product.productCategory.name) || [];
    return [allOption, ...new Set(categories)];
  }

  function handleRemovePopularProduct(product: Product) {
    if (!popularProducts.length) {
      return;
    }

    const updatedData = removePopularProduct(product, popularProducts);
    handleSaveChanges(updatedData);
  }

  async function handleSaveChanges(data: PopularProduct[] = []) {
    const updatedPopularProducts =
      data
        .filter((p) => p.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((p, index) => ({
          ...p,
          displayOrder: index + 1,
          product: undefined,
        })) ?? [];

    if (!updatedPopularProducts.length) {
      showAlert('Error saving changes, please refresh and try again.', 'error');
      return;
    }
    const postResult = await popularProductsApi.createOrUpdate(
      updatedPopularProducts,
    );

    if (postResult.status !== 200) {
      showAlert('Error saving changes, please try again.', 'error');
      return;
    }
    fetchPopularProducts();
    showAlert('Changes saved successfully.', 'success');
  }

  function handlePageChange(_: React.ChangeEvent<unknown>, value: number) {
    setCurrentPage(value);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }

  const filteredProducts =
    products.filter(
      (product) =>
        (filterText
          ? product.name.toLowerCase().includes(filterText.toLowerCase())
          : true) &&
        (selectedCategory && selectedCategory !== allOption
          ? product.productCategory.name === selectedCategory
          : true) &&
        (showAllProducts ||
          popularProducts.some(
            (p) => p.productId === product.id && p.isActive,
          )),
    ) || [];

  const totalNumberOfPages = Math.ceil(
    filteredProducts.length / productsPerPage,
  );

  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage,
  );

  return (
    <Stack
      sx={{
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <AdminHeader
        title="Popular Products"
        description="Manage your popular products, search add or remove products."
        icon={<GradeIcon fontSize="large" sx={{ color: 'white' }} />}
      />

      <Stack
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <PopularProductFilter
          filterText={filterText}
          setFilterText={setFilterText}
          selectedCategory={selectedCategory}
          setSelectedCategory={(category) => {
            setSelectedCategory(category);
            setCurrentPage(1);
          }}
          showAllProducts={showAllProducts}
          setShowAllProducts={setShowAllProducts}
          getUniqueProductCategories={getUniqueProductCategories}
        />

        <Box
          ref={tableContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <PopularProductTable
            isLoading={isLoading}
            paginatedProducts={paginatedProducts}
            popularProductsData={popularProducts}
            showAllProducts={showAllProducts}
            handleOrderChange={handleOrderChange}
            addProduct={handleAddProduct}
            removePopularProduct={handleRemovePopularProduct}
          />
        </Box>

        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            py: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Stack spacing={2} direction="row" justifyContent="center">
            <Pagination
              count={totalNumberOfPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
            />
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}
